if (!customElements.get('heating-benefits')) {
  customElements.define('heating-benefits', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.abortController) return;
      this.track = this.querySelector('[data-benefit-track]');
      this.dialog = this.querySelector('[data-benefit-dialog]');
      this.cards = Array.from(this.querySelectorAll('[data-benefit-open]'));
      if (!this.track || !this.dialog) return;

      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.navigation = this.querySelector('[data-benefit-navigation]');
      this.previous = this.querySelector('[data-benefit-prev]');
      this.next = this.querySelector('[data-benefit-next]');
      this.dialogPrevious = this.querySelector('[data-benefit-dialog-prev]');
      this.dialogNext = this.querySelector('[data-benefit-dialog-next]');
      this.largeImage = this.querySelector('[data-benefit-large-image]');
      this.stage = this.querySelector('[data-benefit-stage]');
      this.zoomButton = this.querySelector('[data-benefit-zoom]');
      this.zoomHelp = this.querySelector('[data-benefit-zoom-help]');
      this.counter = this.querySelector('[data-benefit-counter]');
      this.activeIndex = 0;

      this.addEventListener('click', event => this.onClick(event), options);
      this.track.addEventListener('keydown', event => this.onTrackKeydown(event), options);
      this.track.addEventListener('scroll', () => this.updateNavigation(), { ...options, passive: true });
      this.dialog.addEventListener('keydown', event => this.onDialogKeydown(event), options);
      this.dialog.addEventListener('close', () => this.onDialogClose(), options);
      this.largeImage.addEventListener('load', () => {
        this.dialog.style.setProperty('--heating-benefits-image-width', `${this.largeImage.naturalWidth}px`);
        this.zoomButton.disabled = false;
      }, options);
      this.largeImage.addEventListener('error', () => {
        this.zoomButton.disabled = true;
        this.zoomHelp.textContent = 'This image could not load. Please try another image.';
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
      this.resizeObserver.observe(this.track);
      this.updateNavigation();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
    }

    onClick(event) {
      const trigger = event.target.closest('[data-benefit-open]');
      if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0 && typeof this.dialog.showModal === 'function') {
        event.preventDefault();
        this.lastTrigger = trigger;
        this.showImage(this.cards.indexOf(trigger));
        this.dialog.showModal();
        return;
      }
      if (event.target.closest('[data-benefit-prev]')) this.scrollCards(-1);
      if (event.target.closest('[data-benefit-next]')) this.scrollCards(1);
      if (event.target.closest('[data-benefit-dialog-prev]')) this.showImage(this.activeIndex - 1);
      if (event.target.closest('[data-benefit-dialog-next]')) this.showImage(this.activeIndex + 1);
      if (event.target.closest('[data-benefit-close]')) this.dialog.close();
      if (event.target.closest('[data-benefit-zoom]')) this.toggleZoom();
      if (event.target === this.dialog) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }
    }

    scrollCards(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.scrollBehavior() });
    }

    scrollBehavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    updateNavigation() {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.navigation.hidden = maximum < 3;
      this.previous.disabled = this.track.scrollLeft < 3;
      this.next.disabled = this.track.scrollLeft >= maximum - 3;
    }

    onTrackKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollCards(event.key === 'ArrowLeft' ? -1 : 1);
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: this.scrollBehavior() });
      }
    }

    showImage(index) {
      if (index < 0 || index >= this.cards.length) return;
      this.activeIndex = index;
      const card = this.cards[index];
      this.setZoom(false);
      this.largeImage.alt = card.dataset.imageAlt || card.querySelector('img').alt;
      this.largeImage.src = card.href;
      this.zoomButton.disabled = !this.largeImage.complete;
      this.counter.textContent = `${index + 1} / ${this.cards.length}`;
      this.dialogPrevious.disabled = index === 0;
      this.dialogNext.disabled = index === this.cards.length - 1;
      this.stage.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    toggleZoom() {
      this.setZoom(!this.dialog.classList.contains('is-zoomed'));
      this.stage.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (this.dialog.classList.contains('is-zoomed')) this.stage.focus({ preventScroll: true });
    }

    setZoom(zoomed) {
      this.dialog.classList.toggle('is-zoomed', zoomed);
      this.zoomButton.setAttribute('aria-pressed', String(zoomed));
      this.zoomButton.textContent = zoomed ? 'Fit image' : 'Zoom in';
      this.zoomHelp.textContent = zoomed ? 'Scroll to read the enlarged image.' : 'Select Zoom in for larger text.';
    }

    onDialogKeydown(event) {
      if (this.dialog.classList.contains('is-zoomed')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.showImage(this.activeIndex + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    }

    onDialogClose() {
      this.setZoom(false);
      if (this.lastTrigger?.isConnected) this.lastTrigger.focus({ preventScroll: true });
    }
  });
}
